import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from './modal';
import { Icon } from './icon';
import { api } from '@/lib/api';
import { useUI } from '@/stores/ui';
import { useMemo, useState, useEffect, useRef } from 'react';
import type { Settings, Task, Chunk, Block } from '@focus/shared';
import { weekMondayFromOffset, scoreFor, parseEst } from '@focus/shared';
import { cn } from '@/lib/cn';
import { BLOCK_CAT_ORDER, BLOCK_TONES, toneFor } from './block-tones';
import { scoreTone } from './app-colors';

export function Modals() {
  const { modal, modalTaskId, modalBlockId, closeModal } = useUI();
  return (
    <>
      <BriefingModal open={modal === 'briefing'} onClose={closeModal} />
      <OverviewModal open={modal === 'overview'} onClose={closeModal} />
      <SettingsModal open={modal === 'settings'} onClose={closeModal} />
      <TaskDetailModal open={modal === 'taskDetail'} onClose={closeModal} taskId={modalTaskId} />
      <BlockDetailModal open={modal === 'blockDetail'} onClose={closeModal} blockId={modalBlockId} />
      <ExportModal open={modal === 'export'} onClose={closeModal} />
    </>
  );
}

function ExportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: tasks = [] } = useQuery({ queryKey: ['tasks'], queryFn: api.listTasks, enabled: open });
  const setToast = useUI((s) => s.setToast);

  const openTasks = useMemo(() => tasks.filter((t) => !t.done), [tasks]);

  const prioSorted = useMemo(() => {
    return [...openTasks].sort((a, b) => {
      const sa = scoreFor(a) ?? -1;
      const sb = scoreFor(b) ?? -1;
      if (sb !== sa) return sb - sa;
      const da = a.due ? Date.parse(a.due) : Infinity;
      const db = b.due ? Date.parse(b.due) : Infinity;
      return da - db;
    });
  }, [openTasks]);

  const stamp = () => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
  };

  function csvEscape(v: unknown): string {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes(';')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  }

  function downloadCsv(filename: string, content: string) {
    const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function flash(msg: string, tone: 'accent' | 'ok' | 'bad' = 'ok') {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 1800);
  }

  function exportMinimal() {
    const rows = [['Titel', 'Thema'], ...prioSorted.map((t) => [t.title, t.tag || 'Inbox'])];
    const csv = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
    downloadCsv(`tasks-${stamp()}.csv`, csv);
    flash('CSV geladen', 'ok');
    onClose();
  }

  function exportDetailed() {
    const header = [
      'Titel', 'Thema', 'Score', 'Gruppe', 'Schaetzung',
      'Faellig', 'Impact', 'Urgency', 'UrgDays', 'Chunks',
      'ChunkTitel', 'Quelle', 'Kind', 'Angelegt', 'Aktualisiert',
    ];
    const rows = prioSorted.map((t) => {
      const chunks = Array.isArray(t.chunks) ? t.chunks : [];
      return [
        t.title,
        t.tag || 'Inbox',
        scoreFor(t) ?? '',
        t.group,
        t.est,
        t.due,
        t.impact ?? '',
        t.urgency ?? '',
        t.urgDays ?? '',
        chunks.length,
        chunks.map((c) => c.title).join(' | '),
        (t as any).source || '',
        t.kind,
        new Date(t.createdAt).toISOString(),
        new Date(t.updatedAt).toISOString(),
      ];
    });
    const csv = [header, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
    downloadCsv(`tasks-detail-${stamp()}.csv`, csv);
    flash('CSV mit Details geladen', 'ok');
    onClose();
  }

  function buildClipboardText() {
    const lines: string[] = [];
    lines.push(`Aufgabenliste - priorisiert (${prioSorted.length} offene Tasks, Stand ${new Date().toLocaleString('de-DE')})`);
    lines.push('');
    let prevTag = '';
    prioSorted.forEach((t, i) => {
      const tag = t.tag || 'Inbox';
      if (tag !== prevTag) {
        if (i > 0) lines.push('');
        lines.push(`# ${tag}`);
        prevTag = tag;
      }
      const score = scoreFor(t);
      const parts = [`${i + 1}.`, t.title];
      const meta: string[] = [];
      if (score != null) meta.push(`Score ${score}`);
      if (t.est) meta.push(`${t.est}`);
      if (t.due) meta.push(`fällig ${t.due}`);
      if (t.urgDays != null) meta.push(`${t.urgDays}d`);
      if (meta.length) parts.push('(' + meta.join(', ') + ')');
      lines.push(parts.join(' '));
      const chunks = Array.isArray(t.chunks) ? t.chunks : [];
      chunks.forEach((c) => {
        lines.push(`   - ${c.done ? '[x]' : '[ ]'} ${c.title}${c.est ? ' (' + c.est + ')' : ''}`);
      });
    });
    return lines.join('\n');
  }

  async function copyToClipboard() {
    const text = buildClipboardText();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      flash(`${prioSorted.length} Tasks in Zwischenablage`, 'ok');
      onClose();
    } catch (e) {
      flash('Kopieren fehlgeschlagen', 'bad');
    }
  }

  const totalChunks = useMemo(() => openTasks.reduce((s, t) => s + (Array.isArray(t.chunks) ? t.chunks.length : 0), 0), [openTasks]);

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title="Liste exportieren" maxWidth={520}>
      <div className="space-y-4">
        <div className="text-[13px] text-fg dark:text-dark-fg">
          {openTasks.length} offene {openTasks.length === 1 ? 'Aufgabe' : 'Aufgaben'}
          {totalChunks > 0 && <> mit {totalChunks} Sub-Chunks</>}, sortiert nach Score (Hoch → Niedrig).
        </div>

        <div className="grid gap-2">
          <button
            type="button"
            onClick={exportMinimal}
            className="flex items-start gap-3 p-3 rounded-lg border border-line dark:border-dark-line hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition text-left"
          >
            <div className="shrink-0 w-8 h-8 rounded-md bg-paperAlt dark:bg-dark-paperAlt flex items-center justify-center">
              <Icon name="download" size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold tracking-[-0.1px]">CSV (nur Titel + Thema)</div>
              <div className="text-[12px] text-fg dark:text-dark-fg opacity-75 mt-0.5">
                Eine Zeile pro Aufgabe, 2 Spalten. Für schnellen Import in Sheets oder Notion.
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={exportDetailed}
            className="flex items-start gap-3 p-3 rounded-lg border border-line dark:border-dark-line hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition text-left"
          >
            <div className="shrink-0 w-8 h-8 rounded-md bg-paperAlt dark:bg-dark-paperAlt flex items-center justify-center">
              <Icon name="fileText" size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold tracking-[-0.1px]">CSV mit allen Details</div>
              <div className="text-[12px] text-fg dark:text-dark-fg opacity-75 mt-0.5">
                15 Spalten: Score, Gruppe, Schätzung, Deadline, Impact, Urgency, Chunks, Quelle, Zeitstempel.
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={copyToClipboard}
            className="flex items-start gap-3 p-3 rounded-lg border border-line dark:border-dark-line hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition text-left"
          >
            <div className="shrink-0 w-8 h-8 rounded-md bg-paperAlt dark:bg-dark-paperAlt flex items-center justify-center">
              <Icon name="copy" size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold tracking-[-0.1px]">In Zwischenablage (priorisiert)</div>
              <div className="text-[12px] text-fg dark:text-dark-fg opacity-75 mt-0.5">
                Markdown-ähnlicher Text, nach Score sortiert, Themen gruppiert, inkl. Sub-Chunks.
              </div>
            </div>
          </button>
        </div>
      </div>
    </Modal>
  );
}

function BriefingModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: api.getSettings, enabled: open });
  const { data: tasks = [] } = useQuery({ queryKey: ['tasks'], queryFn: api.listTasks, enabled: open });
  const today = new Date();
  const weekStart = weekMondayFromOffset(0).getTime();
  const { data: blocks = [] } = useQuery({
    queryKey: ['blocks', weekStart],
    queryFn: () => api.listBlocks(weekStart),
    enabled: open,
  });
  const todayDayIdx = ((today.getDay() + 6) % 7);
  const todayBlocks = blocks.filter((b) => b.dayIdx === todayDayIdx && !b.done);
  const urgent = tasks.filter((t) => !t.done && t.urgDays != null && t.urgDays <= 1).slice(0, 5);

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title={`Guten Morgen, ${settings?.userName || 'Tom'}`} maxWidth={600}>
      <div className="space-y-5">
        <section>
          <div className="text-[13px] tracking-[-0.1px] text-fg dark:text-dark-fg font-semibold mb-2">Heute</div>
          {todayBlocks.length === 0 ? (
            <div className="text-sm text-fg dark:text-dark-fg">Noch keine Blöcke geplant. Zieh eine Task aus der Sidebar in den Kalender.</div>
          ) : (
            <ul className="space-y-1.5">
              {todayBlocks.map((b) => (
                <li key={b.id} className="flex items-start gap-2 text-sm">
                  <span className="font-mono text-[11px] text-fg dark:text-dark-fg w-24 shrink-0 mt-0.5">
                    {fmtHour(b.s)}–{fmtHour(b.e)}
                  </span>
                  <span className={cn('leading-snug break-words', b.source === 'gcal' && 'text-impact')}>{b.t}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <div className="text-[13px] tracking-[-0.1px] text-fg dark:text-dark-fg font-semibold mb-2">Deadlines diese Woche</div>
          {urgent.length === 0 ? (
            <div className="text-sm text-fg dark:text-dark-fg">Nichts akut.</div>
          ) : (
            <ul className="space-y-1">
              {urgent.map((t) => (
                <li key={t.id} className="text-sm leading-snug break-words">
                  <span className="font-medium">{t.title}</span>
                  <span className="ml-2 text-xs text-bad">{t.urgDays === 0 ? 'heute' : t.urgDays === 1 ? 'morgen' : `in ${t.urgDays}T`}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="flex justify-end">
          <button onClick={onClose} className="btn-primary">Los geht's</button>
        </div>
      </div>
    </Modal>
  );
}

type AnalyticsRange = 'today' | 'week' | 'month';

function rangeBounds(range: AnalyticsRange): { from: number; to: number; label: string; days: number } {
  const now = new Date();
  if (range === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { from: start.getTime(), to: end.getTime(), label: 'Heute', days: 1 };
  }
  if (range === 'week') {
    const monday = weekMondayFromOffset(0);
    const next = new Date(monday);
    next.setDate(next.getDate() + 7);
    return { from: monday.getTime(), to: next.getTime(), label: 'Diese Woche', days: 7 };
  }
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 29);
  const end = new Date(now);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() + 1);
  return { from: start.getTime(), to: end.getTime(), label: 'Letzte 30 Tage', days: 30 };
}

function formatHm(sec: number): string {
  if (sec < 60) return '0m';
  const m = Math.round(sec / 60);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 0) return `${mm}m`;
  if (mm === 0) return `${h}h`;
  return `${h}h ${mm}m`;
}

function OverviewModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [tab, setTab] = useState<'today' | 'analytics'>('today');
  const [range, setRange] = useState<AnalyticsRange>('week');
  const weekStart = weekMondayFromOffset(0).getTime();
  const { data: tasks = [] } = useQuery({ queryKey: ['tasks'], queryFn: api.listTasks, enabled: open });
  const { data: blocks = [] } = useQuery({
    queryKey: ['blocks', weekStart],
    queryFn: () => api.listBlocks(weekStart),
    enabled: open,
  });

  const bounds = useMemo(() => rangeBounds(range), [range]);
  const { data: summary } = useQuery({
    queryKey: ['activity-summary', bounds.from, bounds.to],
    queryFn: () => api.getActivitySummary({ from: bounds.from, to: bounds.to }),
    enabled: open && tab === 'analytics',
  });

  const totalH = blocks.reduce((s, b) => s + (b.e - b.s), 0);
  const doneH = blocks.filter((b) => b.done).reduce((s, b) => s + (b.e - b.s), 0);

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title="Übersicht" maxWidth={820}>
      <div className="flex gap-2 mb-4 border-b border-line dark:border-dark-line pb-2">
        {(['today', 'analytics'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'px-3 py-1 text-sm rounded-md transition',
              tab === t
                ? 'bg-fg text-paper dark:bg-dark-fg dark:text-dark-bg'
                : 'text-fg dark:text-dark-fg hover:bg-paperAlt dark:hover:bg-dark-paperAlt',
            )}
          >
            {t === 'today' ? 'Heute' : 'Analytics'}
          </button>
        ))}
      </div>

      {tab === 'today' ? (
        <div className="space-y-4">
          <Stat label="Tasks offen" value={String(tasks.filter((t) => !t.done).length)} />
          <Stat label="Geplant (Woche)" value={`${totalH.toFixed(1)} h`} />
          <Stat label="Davon erledigt" value={`${doneH.toFixed(1)} h`} />
        </div>
      ) : (
        <AnalyticsView
          range={range}
          setRange={setRange}
          bounds={bounds}
          summary={summary}
          tasks={tasks}
          blocks={blocks}
        />
      )}
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-line dark:border-dark-line last:border-0">
      <span className="text-sm text-fg dark:text-dark-fg">{label}</span>
      <span className="font-mono font-semibold">{value}</span>
    </div>
  );
}

function AnalyticsView({
  range,
  setRange,
  bounds,
  summary,
  tasks,
  blocks,
}: {
  range: AnalyticsRange;
  setRange: (r: AnalyticsRange) => void;
  bounds: { from: number; to: number; label: string; days: number };
  summary: import('@focus/shared').ActivitySummary | undefined;
  tasks: Task[];
  blocks: Block[];
}) {
  const totalSec = summary?.totalSec ?? 0;
  const tracedH = totalSec / 3600;

  const planSec = blocks
    .filter((b) => b.s != null && b.e != null)
    .reduce((acc, b) => {
      const dStart = weekMondayFromOffset(0).getTime() + b.dayIdx * 86400000;
      if (dStart >= bounds.from && dStart < bounds.to) {
        return acc + (b.e - b.s) * 3600;
      }
      return acc;
    }, 0);

  const sortedCats = useMemo(() => {
    if (!summary) return [] as Array<{ cat: string; sec: number }>;
    return BLOCK_CAT_ORDER
      .map((cat) => ({ cat, sec: summary.byCategory[cat] ?? 0 }))
      .filter((x) => x.sec > 0)
      .sort((a, b) => b.sec - a.sec);
  }, [summary]);

  const taskMap = useMemo(() => {
    const m = new Map<string, Task>();
    tasks.forEach((t) => m.set(t.id, t));
    return m;
  }, [tasks]);

  const dayLabels = useMemo(() => {
    const days: Array<{ ms: number; label: string }> = [];
    if (range === 'today') {
      const d = new Date(bounds.from);
      days.push({ ms: bounds.from, label: d.toLocaleDateString('de-DE', { weekday: 'short' }) });
    } else if (range === 'week') {
      const wd = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
      for (let i = 0; i < 7; i++) {
        days.push({ ms: bounds.from + i * 86400000, label: wd[i] });
      }
    } else {
      // Letzte 30 Tage: show every day
      for (let i = 0; i < 30; i++) {
        const d = new Date(bounds.from + i * 86400000);
        days.push({ ms: d.getTime(), label: String(d.getDate()) });
      }
    }
    return days;
  }, [range, bounds]);

  const dayMap = useMemo(() => {
    const m = new Map<number, { totalSec: number; byCategory: Record<string, number> }>();
    summary?.byDay.forEach((d) => m.set(d.dayMs, { totalSec: d.totalSec, byCategory: d.byCategory }));
    return m;
  }, [summary]);

  const maxDaySec = useMemo(() => {
    let max = 0;
    summary?.byDay.forEach((d) => { if (d.totalSec > max) max = d.totalSec; });
    return max || 1;
  }, [summary]);

  const deepSec = (summary?.byCategory['blockDeep'] ?? 0) + (summary?.byCategory['blockCreative'] ?? 0);
  const deepPct = totalSec > 0 ? Math.round((deepSec / totalSec) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Range selector */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center p-[3px] bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-full gap-[2px]">
          {(['today', 'week', 'month'] as const).map((r) => {
            const active = range === r;
            return (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={cn(
                  'h-6 px-3 rounded-full text-[12.5px] font-medium transition whitespace-nowrap',
                  active
                    ? 'bg-fg text-paper dark:bg-dark-fg dark:text-dark-bg'
                    : 'text-fg dark:text-dark-fg hover:bg-paperAlt dark:hover:bg-dark-paperAlt',
                )}
              >
                {r === 'today' ? 'Heute' : r === 'week' ? 'Woche' : '30 Tage'}
              </button>
            );
          })}
        </div>
        <div className="text-[11px] font-mono text-fg dark:text-dark-fg opacity-60">
          {bounds.label}
        </div>
      </div>

      {!summary ? (
        <div className="py-8 text-center text-[13px] text-fg dark:text-dark-fg opacity-60">
          Lade Aktivitätsdaten…
        </div>
      ) : totalSec === 0 ? (
        <div className="py-8 text-center text-[13px] text-fg dark:text-dark-fg opacity-60">
          Keine Aktivität in diesem Zeitraum getrackt.<br />
          <span className="text-[11px] opacity-70">Der Activity-Tracker schreibt erst Daten, sobald er läuft.</span>
        </div>
      ) : (
        <>
          {/* Headline KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <KpiCard label="Aktiv getrackt" value={formatHm(totalSec)} sub={`${range === 'today' ? '' : `Ø ${formatHm(totalSec / Math.max(1, summary.byDay.length))}/Tag`}`} />
            <KpiCard label="Geplant (Blöcke)" value={`${(planSec / 3600).toFixed(1)}h`} sub={planSec > 0 ? `${Math.round((totalSec / planSec) * 100)}% genutzt` : '—'} />
            <KpiCard label="Deep Work" value={`${deepPct}%`} sub={formatHm(deepSec)} />
            <KpiCard label="Top Kategorie" value={sortedCats[0] ? (BLOCK_TONES[sortedCats[0].cat]?.label ?? sortedCats[0].cat) : '—'} sub={sortedCats[0] ? formatHm(sortedCats[0].sec) : ''} />
          </div>

          {/* Per-day stacked bars */}
          <section>
            <div className="text-[12.5px] tracking-[-0.1px] text-fg dark:text-dark-fg font-semibold mb-2">
              Pro Tag
            </div>
            <div className={cn(
              'flex items-end gap-1 border-b border-line dark:border-dark-line pb-1',
              range === 'month' ? 'h-[70px]' : 'h-[120px]',
            )}>
              {dayLabels.map((d, idx) => {
                const day = dayMap.get(d.ms);
                const tot = day?.totalSec ?? 0;
                const heightPct = (tot / maxDaySec) * 100;
                return (
                  <div key={idx} className="flex-1 min-w-0 flex flex-col items-stretch h-full">
                    <div className="flex-1 flex flex-col-reverse rounded-t-sm overflow-hidden bg-paperAlt dark:bg-dark-paperAlt relative">
                      {day && (
                        <div className="absolute inset-x-0 bottom-0 flex flex-col-reverse" style={{ height: `${heightPct}%` }}>
                          {BLOCK_CAT_ORDER.map((cat) => {
                            const sec = day.byCategory[cat] ?? 0;
                            if (sec === 0) return null;
                            const tone = toneFor(cat);
                            const segPct = (sec / tot) * 100;
                            return (
                              <div
                                key={cat}
                                title={`${tone.label}: ${formatHm(sec)}`}
                                className="w-full"
                                style={{
                                  height: `${segPct}%`,
                                  background: `var(--tone, ${tone.swatch})`,
                                  // @ts-expect-error CSS var
                                  '--tone': `light-dark(${tone.swatch}, ${tone.swatchDark})`,
                                }}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-end gap-1 mt-1">
              {dayLabels.map((d, idx) => (
                <div key={idx} className="flex-1 min-w-0 text-center text-[10px] font-mono tabular-nums text-fg dark:text-dark-fg opacity-60">
                  {d.label}
                </div>
              ))}
            </div>
          </section>

          {/* Category breakdown */}
          <section>
            <div className="text-[12.5px] tracking-[-0.1px] text-fg dark:text-dark-fg font-semibold mb-2">
              Kategorien
            </div>
            <div className="space-y-1.5">
              {sortedCats.map(({ cat, sec }) => {
                const tone = toneFor(cat);
                const pct = (sec / totalSec) * 100;
                return (
                  <div key={cat} className="flex items-center gap-3 text-[12px]">
                    <div className="flex items-center gap-2 w-32 shrink-0">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: tone.swatch }} />
                      <span className="text-fg dark:text-dark-fg truncate">{tone.label}</span>
                    </div>
                    <div className="flex-1 h-2 rounded bg-paperAlt dark:bg-dark-paperAlt overflow-hidden">
                      <div className="h-full" style={{ width: `${pct}%`, background: tone.swatch }} />
                    </div>
                    <div className="w-20 text-right font-mono text-[11px] text-fg dark:text-dark-fg">
                      {formatHm(sec)}
                    </div>
                    <div className="w-10 text-right font-mono text-[10.5px] text-fg dark:text-dark-fg opacity-60">
                      {pct.toFixed(0)}%
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Top apps with expandable titles */}
          <section>
            <div className="text-[12.5px] tracking-[-0.1px] text-fg dark:text-dark-fg font-semibold mb-2">
              Top Apps
            </div>
            <div className="space-y-0">
              {summary.byApp.slice(0, 15).map((app) => (
                <AppRow key={app.app} app={app} totalSec={totalSec} />
              ))}
              {summary.byApp.length === 0 && (
                <div className="text-[11.5px] text-fg dark:text-dark-fg opacity-60">Keine App-Daten.</div>
              )}
            </div>
          </section>

          {/* Top tasks (linked from activity events) */}
          {summary.byTask.length > 0 && (
            <section>
              <div className="text-[12.5px] tracking-[-0.1px] text-fg dark:text-dark-fg font-semibold mb-2">
                Top Tasks
              </div>
              <div className="space-y-1">
                {summary.byTask.slice(0, 10).map((t) => {
                  const task = taskMap.get(t.taskId);
                  const pct = (t.sec / totalSec) * 100;
                  return (
                    <div key={t.taskId} className="flex items-center gap-3 text-[12px]">
                      <div className="flex-1 truncate text-fg dark:text-dark-fg">
                        {task?.title ?? <span className="opacity-50 font-mono">{t.taskId.slice(0, 8)}…</span>}
                      </div>
                      <div className="w-32 h-1.5 rounded bg-paperAlt dark:bg-dark-paperAlt overflow-hidden">
                        <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-20 text-right font-mono text-[11px] text-fg dark:text-dark-fg">
                        {formatHm(t.sec)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-line dark:border-dark-line bg-paper dark:bg-dark-paper p-3 min-w-0">
      <div className="text-[10.5px] uppercase tracking-[0.6px] text-fg dark:text-dark-fg opacity-50 mb-1 truncate">
        {label}
      </div>
      <div className="text-[18px] font-semibold tracking-[-0.3px] text-fg dark:text-dark-fg leading-tight truncate">
        {value}
      </div>
      {sub && (
        <div className="text-[10.5px] font-mono tabular-nums text-fg dark:text-dark-fg opacity-60 mt-0.5 truncate">
          {sub}
        </div>
      )}
    </div>
  );
}

function AppRow({
  app,
  totalSec,
}: {
  app: { app: string; sec: number; titles: Array<{ title: string | null; sec: number }> };
  totalSec: number;
}) {
  const [open, setOpen] = useState(false);
  const pct = (app.sec / totalSec) * 100;
  return (
    <div className="border-b border-line/60 dark:border-dark-line/60 last:border-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 py-1.5 text-[12px] hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition rounded px-1 -mx-1 text-left"
      >
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={10} className="opacity-50 shrink-0" />
        <div className="w-40 truncate text-fg dark:text-dark-fg">{app.app}</div>
        <div className="flex-1 h-1.5 rounded bg-paperAlt dark:bg-dark-paperAlt overflow-hidden">
          <div className="h-full bg-fg dark:bg-dark-fg" style={{ width: `${pct}%` }} />
        </div>
        <div className="w-20 text-right font-mono text-[11px] text-fg dark:text-dark-fg">
          {formatHm(app.sec)}
        </div>
      </button>
      {open && app.titles.length > 0 && (
        <div className="pl-6 pb-2 space-y-0.5">
          {app.titles.map((t, idx) => (
            <div key={idx} className="flex items-center gap-2 text-[11px] text-fg dark:text-dark-fg opacity-80">
              <div className="flex-1 truncate">
                {t.title || <span className="opacity-60">– kein Titel –</span>}
              </div>
              <div className="font-mono tabular-nums">{formatHm(t.sec)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: api.getSettings, enabled: open });
  const { data: gstatus } = useQuery({ queryKey: ['gstatus'], queryFn: api.googleStatus, enabled: open });
  const { data: cals = [] } = useQuery({ queryKey: ['gcals'], queryFn: api.gcalCalendars, enabled: open && !!gstatus?.connected });

  const [userName, setUserName] = useState('');
  useEffect(() => setUserName(settings?.userName ?? 'Tom'), [settings?.userName]);

  const save = useMutation({
    mutationFn: (p: Partial<Settings>) => api.putSettings(p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
  const sync = useMutation({
    mutationFn: api.gcalSync,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gcals'] });
      qc.invalidateQueries({ queryKey: ['blocks'] });
    },
  });
  const togCal = useMutation({
    mutationFn: (v: { id: string; selected: boolean }) => api.gcalPatch(v.id, { selected: v.selected }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gcals'] }),
  });
  const disconnect = useMutation({
    mutationFn: api.googleDisconnect,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gstatus'] }),
  });

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title="Einstellungen" maxWidth={620}>
      <div className="space-y-6">
        <section>
          <div className="text-[13px] tracking-[-0.1px] text-fg dark:text-dark-fg font-semibold mb-2">Dein Name</div>
          <input
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            onBlur={() => userName !== settings?.userName && save.mutate({ userName })}
            className="w-full px-3 py-2 text-sm rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent input-bare"
          />
        </section>

        <section>
          <div className="text-[13px] tracking-[-0.1px] text-fg dark:text-dark-fg font-semibold mb-2">Google Calendar</div>
          {!gstatus?.configured ? (
            <div className="p-3 rounded-md bg-warn/20 text-sm">
              <div className="font-medium mb-1">Nicht konfiguriert</div>
              <div className="text-xs text-fg dark:text-dark-fg space-y-1.5">
                <div>
                  1. Google Cloud Console → APIs & Services → Credentials → OAuth client ID (Type: <b>Web application</b>).
                </div>
                <div>
                  2. Authorized redirect URI: <code>http://localhost:8765/api/auth/google/callback</code>
                </div>
                <div>
                  3. In <code>apps/api/.env</code> setzen:
                  <pre className="mt-1 p-1.5 bg-paperAlt dark:bg-dark-paperAlt rounded text-[10.5px] font-mono leading-tight">
                    GOOGLE_CLIENT_ID=…{'\n'}GOOGLE_CLIENT_SECRET=…{'\n'}GOOGLE_REDIRECT_URI=http://localhost:8765/api/auth/google/callback
                  </pre>
                </div>
                <div>4. API neu starten.</div>
              </div>
            </div>
          ) : !gstatus.connected ? (
            <a href="/api/auth/google/start" className="btn-primary w-full justify-center">
              <Icon name="calendar" size={14} /> Google Calendar verbinden
            </a>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[11.5px] text-fg dark:text-dark-fg">
                {gstatus.accountEmail && (
                  <span className="font-mono">{gstatus.accountEmail}</span>
                )}
                <span className="opacity-60">·</span>
                <span>
                  Letzter Sync: {fmtRelative(gstatus.lastSync)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn-ghost text-sm">
                  <Icon name="rotate" size={14} /> {sync.isPending ? 'Synchronisiere…' : 'Jetzt synchronisieren'}
                </button>
                <button onClick={() => disconnect.mutate()} className="btn-ghost text-sm text-bad">
                  <Icon name="x" size={14} /> Trennen
                </button>
              </div>
              <div className="space-y-1">
                {cals.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-line/30">
                    <input
                      type="checkbox"
                      checked={c.selected}
                      onChange={(e) => togCal.mutate({ id: c.id, selected: e.target.checked })}
                    />
                    <span className="flex-1">{c.summary}</span>
                    {c.primary && <span className="text-[10px] text-fg dark:text-dark-fg">primary</span>}
                  </label>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Task Detail Modal
// ──────────────────────────────────────────────────────────────────────

const KIND_LABELS: Record<string, string> = {
  task: 'Aufgabe',
  event: 'Termin',
  habit: 'Routine',
  note: 'Notiz',
};

function parseDue(due: string | undefined): { date: string; time: string } {
  if (!due) return { date: '', time: '' };
  const m = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?$/.exec(due);
  if (m) return { date: m[1], time: m[2] ?? '' };
  return { date: '', time: '' };
}

function composeDue(date: string, time: string): string {
  if (!date) return '';
  if (!time) return date;
  return `${date}T${time}`;
}

function daysUntil(due: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(due);
  if (!m) return null;
  const target = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 86400000);
}

function TaskDetailModal({ open, onClose, taskId }: { open: boolean; onClose: () => void; taskId: string | null }) {
  const qc = useQueryClient();
  const { data: tasks = [] } = useQuery({ queryKey: ['tasks'], queryFn: api.listTasks, enabled: open });
  const task = tasks.find((t) => t.id === taskId);

  const patch = useMutation({
    mutationFn: ({ id, p }: { id: string; p: Partial<Task> }) => api.patchTask(id, p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
  const toggleDone = useMutation({
    mutationFn: (id: string) => api.toggleTaskDone(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.deleteTask(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      onClose();
    },
  });

  if (!task) return null;

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title="Details" maxWidth={720}>
      <TaskDetailBody
        task={task}
        onPatch={(p) => patch.mutate({ id: task.id, p })}
        onToggleDone={() => toggleDone.mutate(task.id)}
        onDelete={() => del.mutate(task.id)}
        onClose={onClose}
      />
    </Modal>
  );
}

function TaskDetailBody({
  task, onPatch, onToggleDone, onDelete, onClose,
}: {
  task: Task;
  onPatch: (p: Partial<Task>) => void;
  onToggleDone: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(task.title);
  const { date: initDate, time: initTime } = parseDue(task.due);
  const [dueDate, setDueDate] = useState(initDate);
  const [dueTime, setDueTime] = useState(initTime);
  useEffect(() => { setTitle(task.title); }, [task.id, task.title]);
  useEffect(() => {
    const { date, time } = parseDue(task.due);
    setDueDate(date);
    setDueTime(time);
  }, [task.id, task.due]);

  const score = useMemo(() => scoreFor(task), [task]);

  const commitDue = (d: string, t: string) => {
    const composed = composeDue(d, t);
    if (composed === (task.due ?? '')) return;
    const ud = d ? daysUntil(composed) : null;
    onPatch({ due: composed, urgDays: ud });
  };

  return (
    <div className="space-y-5">
      {/* Header: title + status + score */}
      <div className="flex items-start gap-3">
        <button
          onClick={onToggleDone}
          className={cn(
            'mt-[5px] shrink-0 w-[20px] h-[20px] rounded-[5px] border grid place-items-center transition-all duration-200',
            task.done
              ? 'bg-accent border-accent'
              : 'border-lineStrong dark:border-dark-lineStrong hover:border-accent hover:scale-110',
          )}
          aria-label={task.done ? 'Erledigt rückgängig' : 'Als erledigt markieren'}
        >
          {task.done && <Icon name="check" size={13} className="text-white" />}
        </button>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title !== task.title && onPatch({ title })}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
          className={cn(
            'flex-1 min-w-0 text-[20px] font-semibold tracking-[-0.3px] bg-transparent outline-none border-0 text-fg dark:text-dark-fg leading-snug',
            task.done && 'line-through opacity-60',
          )}
        />
        <ScoreBigBadge
          score={score}
          onCommit={(n) => {
            if (n == null) {
              onPatch({ score: null, impact: null, urgency: null } as any);
              return;
            }
            const snapped = Math.max(0, Math.min(100, Math.round(n / 5) * 5));
            if (snapped === 0) {
              onPatch({ score: null, impact: null, urgency: null } as any);
              return;
            }
            onPatch({ score: snapped } as any);
          }}
        />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <FieldRow label="Typ">
          <SelectBare
            value={task.kind ?? 'task'}
            onChange={(v) => onPatch({ kind: v as Task['kind'] })}
            options={[
              { v: 'task', l: KIND_LABELS.task },
              { v: 'event', l: KIND_LABELS.event },
              { v: 'habit', l: KIND_LABELS.habit },
              { v: 'note', l: KIND_LABELS.note },
            ]}
          />
        </FieldRow>

        <FieldRow label="Tag">
          <input
            defaultValue={task.tag}
            placeholder="Inbox"
            onBlur={(e) => e.target.value !== task.tag && onPatch({ tag: e.target.value || 'Inbox' })}
            className="w-full px-3 py-1.5 text-[13px] rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent input-bare"
          />
        </FieldRow>

        <FieldRow label="Fällig">
          <div className="flex gap-2">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => { setDueDate(e.target.value); commitDue(e.target.value, dueTime); }}
              className="flex-1 px-3 py-1.5 text-[13px] rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent input-bare font-mono"
            />
            <input
              type="time"
              value={dueTime}
              onChange={(e) => { setDueTime(e.target.value); commitDue(dueDate, e.target.value); }}
              className="w-[110px] px-3 py-1.5 text-[13px] rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent input-bare font-mono"
            />
            {(dueDate || dueTime) && (
              <button
                onClick={() => { setDueDate(''); setDueTime(''); commitDue('', ''); }}
                className="btn-ghost text-[11px]"
                title="Fälligkeit entfernen"
              >
                <Icon name="x" size={12} />
              </button>
            )}
          </div>
        </FieldRow>

        <FieldRow label="Geschätzt">
          <input
            defaultValue={task.est}
            placeholder="30m / 1h / 1.5h"
            onBlur={(e) => e.target.value !== task.est && onPatch({ est: e.target.value || '30m' })}
            className="w-full px-3 py-1.5 text-[13px] rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent input-bare font-mono"
          />
        </FieldRow>

        <FieldRow label="Impact (1–10)">
          <ScorePartSelect value={task.impact ?? null} onChange={(v) => onPatch({ impact: v, score: null } as any)} placeholder="Impact wählen …" />
        </FieldRow>

        <FieldRow label="Dringlichkeit (1–10)">
          <ScorePartSelect value={task.urgency ?? null} onChange={(v) => onPatch({ urgency: v, score: null } as any)} placeholder="Dringlichkeit wählen …" />
        </FieldRow>

        <FieldRow label="Deadline">
          {task.urgDays == null ? (
            <span className="text-[12.5px] text-fg dark:text-dark-fg">–</span>
          ) : task.urgDays < 0 ? (
            <span className="text-[12.5px] text-bad font-medium">überfällig ({Math.abs(task.urgDays)}T)</span>
          ) : task.urgDays === 0 ? (
            <span className="text-[12.5px] text-warn font-medium">heute</span>
          ) : task.urgDays === 1 ? (
            <span className="text-[12.5px] text-warn font-medium">morgen</span>
          ) : (
            <span className="text-[12.5px] text-fg dark:text-dark-fg">in {task.urgDays} Tagen</span>
          )}
        </FieldRow>
      </div>

      {/* Chunks */}
      <section className="pt-2 border-t border-line dark:border-dark-line">
        <ChunksPanel task={task} onPatch={onPatch} />
      </section>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2">
        <button onClick={onDelete} className="btn-ghost text-bad text-[12.5px]">
          <Icon name="trash" size={13} /> Task löschen
        </button>
        <button onClick={onClose} className="btn-primary">Fertig</button>
      </div>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] tracking-[-0.1px] text-fg dark:text-dark-fg font-medium">{label}</span>
      {children}
    </label>
  );
}

function SelectBare({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { v: string; l: string }[] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2.5 py-1.5 text-[13px] rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent input-bare cursor-pointer"
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>{o.l}</option>
      ))}
    </select>
  );
}

function ScorePartSelect({
  value, onChange, placeholder,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  placeholder: string;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      className="w-full px-2.5 py-1.5 text-[13px] rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent input-bare cursor-pointer font-mono"
    >
      <option value="">{placeholder}</option>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
  );
}

function ScoreBigBadge({ score, onCommit }: { score: number | null; onCommit: (n: number | null) => void }) {
  const isDark = document.body.classList.contains('dark');
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) {
      setValue(score == null ? '' : String(score));
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [editing, score]);

  const commit = () => {
    setEditing(false);
    const trimmed = value.trim();
    if (trimmed === '') {
      onCommit(null);
      return;
    }
    const n = Number(trimmed);
    if (Number.isFinite(n)) onCommit(n);
  };
  const cancel = () => setEditing(false);

  const clamped = score == null ? null : Math.max(0, Math.min(100, score));
  const tone =
    clamped == null
      ? { bg: 'transparent', fg: 'inherit', bgDark: 'transparent' }
      : scoreTone(clamped);

  if (editing) {
    return (
      <div
        className="shrink-0 flex flex-col items-center justify-center w-[54px] h-[54px] rounded-xl border border-accent"
        style={{ background: clamped == null ? 'transparent' : (isDark ? tone.bgDark : tone.bg) }}
      >
        <input
          ref={inputRef}
          type="number"
          min={0}
          max={100}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          onBlur={commit}
          className="w-[42px] text-center text-[18px] font-bold font-mono leading-none bg-transparent outline-none input-bare p-0"
          style={{ color: clamped == null ? (isDark ? '#fff' : '#000') : (isDark ? '#fff' : tone.fg) }}
        />
        <span className="text-[9px] tracking-[0.3px] mt-0.5 uppercase opacity-80" style={{ color: clamped == null ? (isDark ? '#fff' : '#000') : (isDark ? '#fff' : tone.fg) }}>
          Score
        </span>
      </div>
    );
  }

  if (score == null) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="shrink-0 flex flex-col items-center justify-center w-[54px] h-[54px] rounded-xl border border-dashed border-line dark:border-dark-line opacity-60 hover:opacity-100 hover:border-accent transition"
        title="Score eintragen"
      >
        <span className="text-[18px] font-bold font-mono leading-none text-fg dark:text-dark-fg">–</span>
        <span className="text-[9px] tracking-[0.3px] mt-0.5 uppercase text-fg dark:text-dark-fg">Score</span>
      </button>
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className="shrink-0 flex flex-col items-center justify-center w-[54px] h-[54px] rounded-xl border border-line dark:border-dark-line hover:border-accent transition"
      style={{ background: isDark ? tone.bgDark : tone.bg }}
      title="Score bearbeiten"
    >
      <span className="text-[18px] font-bold font-mono leading-none" style={{ color: isDark ? '#fff' : tone.fg }}>
        {clamped}
      </span>
      <span className="text-[9px] tracking-[0.3px] mt-0.5 uppercase opacity-80" style={{ color: isDark ? '#fff' : tone.fg }}>
        Score
      </span>
    </button>
  );
}

// ─── Block Detail Modal ──────────────────────────────────────────────────

const WEEKDAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function hoursToHHMM(h: number): string {
  const clamped = Math.max(0, Math.min(24, h));
  const hh = Math.floor(clamped);
  const mm = Math.round((clamped - hh) * 60);
  if (mm === 60) return `${String(hh + 1).padStart(2, '0')}:00`;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function hhmmToHours(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh < 0 || hh > 24 || mm < 0 || mm > 59) return null;
  return hh + mm / 60;
}

function fmtDuration(hours: number): string {
  const mins = Math.max(0, Math.round(hours * 60));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function BlockDetailModal({ open, onClose, blockId }: { open: boolean; onClose: () => void; blockId: string | null }) {
  const qc = useQueryClient();
  const { openModal } = useUI();
  const { data: tasks = [] } = useQuery({ queryKey: ['tasks'], queryFn: api.listTasks, enabled: open });

  // Search all cached ['blocks', weekStart] queries for the block, then fall back to fetching by week.
  const cacheFound = useMemo(() => {
    if (!open || !blockId) return null;
    const entries = qc.getQueriesData<Block[]>({ queryKey: ['blocks'] });
    for (const [, data] of entries) {
      const found = data?.find?.((b) => b.id === blockId);
      if (found) return found;
    }
    return null;
  }, [open, blockId, qc, tasks]);

  const { data: fetched } = useQuery({
    queryKey: ['block', blockId],
    queryFn: async () => {
      // Cheap scan of the current and adjacent weeks in case the right cache entry isn't primed.
      const now = Date.now();
      for (let off = -1; off <= 1; off++) {
        const ws = weekMondayFromOffset(off).getTime();
        const list = await api.listBlocks(ws);
        const hit = list.find((b) => b.id === blockId);
        if (hit) return hit;
      }
      return null as Block | null;
    },
    enabled: open && !!blockId && !cacheFound,
    staleTime: 0,
  });

  const block = cacheFound ?? fetched ?? null;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['blocks'] });
    qc.invalidateQueries({ queryKey: ['block', blockId] });
  };

  const patch = useMutation({
    mutationFn: ({ id, p }: { id: string; p: Partial<Block> }) => api.patchBlock(id, p),
    onSuccess: invalidate,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.deleteBlock(id),
    onSuccess: () => { invalidate(); onClose(); },
  });

  if (!block) return null;
  const linkedTask = block.taskId ? tasks.find((t) => t.id === block.taskId) : null;

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title="Termin bearbeiten" maxWidth={640}>
      <BlockDetailBody
        block={block}
        linkedTask={linkedTask ?? null}
        weekStart={block.weekStart}
        onPatch={(p) => patch.mutate({ id: block.id, p })}
        onDelete={() => del.mutate(block.id)}
        onClose={onClose}
        onOpenTask={(taskId) => openModal('taskDetail', taskId)}
      />
    </Modal>
  );
}

function BlockDetailBody({
  block, linkedTask, weekStart, onPatch, onDelete, onClose, onOpenTask,
}: {
  block: Block;
  linkedTask: Task | null;
  weekStart: number;
  onPatch: (p: Partial<Block>) => void;
  onDelete: () => void;
  onClose: () => void;
  onOpenTask: (taskId: string) => void;
}) {
  const qc = useQueryClient();
  const { setToast } = useUI();
  const { data: gstatus } = useQuery({ queryKey: ['gstatus'], queryFn: api.googleStatus });
  const { data: cals = [] } = useQuery({
    queryKey: ['gcals'],
    queryFn: api.gcalCalendars,
    enabled: !!gstatus?.connected,
  });
  const pushableCals = cals.filter((c) => c.selected && c.primary) .length
    ? cals.filter((c) => c.selected && c.primary)
    : cals.filter((c) => c.selected);
  const defaultPushCalId = pushableCals[0]?.id ?? null;
  const pushToGoogle = useMutation({
    mutationFn: (calendarId: string) => api.gcalPush(block.id, calendarId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blocks', weekStart] });
      qc.invalidateQueries({ queryKey: ['block', block.id] });
      setToast({ msg: 'Block zu Google gepusht', tone: 'ok' });
      setTimeout(() => setToast(null), 1800);
    },
    onError: () => {
      setToast({ msg: 'Push nach Google fehlgeschlagen', tone: 'bad' });
      setTimeout(() => setToast(null), 2500);
    },
  });
  const [title, setTitle] = useState(block.t);
  const [startStr, setStartStr] = useState(hoursToHHMM(block.s));
  const [endStr, setEndStr] = useState(hoursToHHMM(block.e));
  useEffect(() => { setTitle(block.t); }, [block.id, block.t]);
  useEffect(() => { setStartStr(hoursToHHMM(block.s)); }, [block.id, block.s]);
  useEffect(() => { setEndStr(hoursToHHMM(block.e)); }, [block.id, block.e]);

  const dayDate = useMemo(() => new Date(weekStart + block.dayIdx * 86400000), [weekStart, block.dayIdx]);
  const readonly = block.source === 'gcal';
  const tone = toneFor(block.cat);
  const isDark = document.body.classList.contains('dark');
  const swatch = isDark ? tone.swatchDark : tone.swatch;

  const commitStart = () => {
    const v = hhmmToHours(startStr);
    if (v == null) { setStartStr(hoursToHHMM(block.s)); return; }
    if (v === block.s) return;
    // If new start ≥ current end, shift end to preserve previous duration.
    const dur = block.e - block.s;
    const p: Partial<Block> = { s: v };
    if (v >= block.e) p.e = Math.min(24, v + dur);
    onPatch(p);
  };
  const commitEnd = () => {
    const v = hhmmToHours(endStr);
    if (v == null) { setEndStr(hoursToHHMM(block.e)); return; }
    if (v === block.e) return;
    // If new end ≤ current start, reject and snap back.
    if (v <= block.s) { setEndStr(hoursToHHMM(block.e)); return; }
    onPatch({ e: v });
  };

  return (
    <div className="space-y-5">
      {/* Header: checkbox + title + color dot */}
      <div className="flex items-start gap-3">
        <button
          onClick={() => !readonly && onPatch({ done: !block.done })}
          disabled={readonly}
          className={cn(
            'mt-[5px] shrink-0 w-[20px] h-[20px] rounded-[5px] border grid place-items-center transition-all duration-200',
            block.done
              ? 'bg-accent border-accent'
              : 'border-lineStrong dark:border-dark-lineStrong hover:border-accent hover:scale-110',
            readonly && 'opacity-50 cursor-not-allowed',
          )}
          aria-label={block.done ? 'Erledigt rückgängig' : 'Als erledigt markieren'}
        >
          {block.done && <Icon name="check" size={13} className="text-white" />}
        </button>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => !readonly && title !== block.t && onPatch({ t: title })}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
          readOnly={readonly}
          className={cn(
            'flex-1 min-w-0 text-[20px] font-semibold tracking-[-0.3px] bg-transparent outline-none border-0 text-fg dark:text-dark-fg leading-snug',
            block.done && 'line-through opacity-60',
            readonly && 'cursor-default',
          )}
        />
        <div
          className="shrink-0 w-[24px] h-[24px] rounded-full border border-black/10 dark:border-white/20 mt-[5px]"
          style={{ background: swatch }}
          title={tone.label}
        />
      </div>

      {/* Two-column field grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <FieldRow label="Tag">
          <SelectBare
            value={String(block.dayIdx)}
            onChange={(v) => !readonly && onPatch({ dayIdx: Number(v) })}
            options={WEEKDAYS.map((name, i) => {
              const d = new Date(weekStart + i * 86400000);
              return { v: String(i), l: `${name}, ${d.getDate()}.${d.getMonth() + 1}.` };
            })}
          />
        </FieldRow>

        <FieldRow label="Datum">
          <div className="px-3 py-1.5 text-[13px] rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent font-mono text-fg dark:text-dark-fg">
            {dayDate.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
          </div>
        </FieldRow>

        <FieldRow label="Start">
          <input
            type="time"
            value={startStr}
            onChange={(e) => setStartStr(e.target.value)}
            onBlur={commitStart}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
            readOnly={readonly}
            className="w-full px-3 py-1.5 text-[13px] rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent input-bare font-mono"
          />
        </FieldRow>

        <FieldRow label="Ende">
          <input
            type="time"
            value={endStr}
            onChange={(e) => setEndStr(e.target.value)}
            onBlur={commitEnd}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
            readOnly={readonly}
            className="w-full px-3 py-1.5 text-[13px] rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent input-bare font-mono"
          />
        </FieldRow>

        <FieldRow label="Dauer">
          <div className="px-3 py-1.5 text-[13px] rounded-md border border-lineStrong dark:border-dark-lineStrong bg-transparent font-mono text-fg dark:text-dark-fg">
            {fmtDuration(block.e - block.s)}
          </div>
        </FieldRow>

        <FieldRow label="Anker">
          <button
            onClick={() => !readonly && onPatch({ anchor: !block.anchor })}
            disabled={readonly}
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 text-[12.5px] rounded-md border transition',
              block.anchor
                ? 'bg-accent border-accent text-white'
                : 'border-line dark:border-dark-line text-fg dark:text-dark-fg hover:border-accent',
              readonly && 'opacity-50 cursor-not-allowed',
            )}
          >
            <Icon name="lock" size={11} />
            <span>{block.anchor ? 'Fixiert' : 'Flexibel'}</span>
          </button>
        </FieldRow>
      </div>

      {/* Color picker */}
      <FieldRow label="Farbe">
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {BLOCK_CAT_ORDER.map((k) => {
            const t = BLOCK_TONES[k];
            const active = k === block.cat;
            const sw = isDark ? t.swatchDark : t.swatch;
            return (
              <button
                key={k}
                onClick={() => !readonly && onPatch({ cat: k })}
                disabled={readonly}
                className={cn(
                  'flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-md border text-[11.5px] tracking-[-0.1px] transition',
                  active
                    ? 'bg-fg border-fg text-paper dark:bg-dark-fg dark:border-dark-fg dark:text-dark-paper font-semibold'
                    : 'border-line dark:border-dark-line text-fg dark:text-dark-fg hover:border-fg dark:hover:border-dark-fg',
                  readonly && 'opacity-60 cursor-not-allowed',
                )}
                title={t.label}
              >
                <span
                  className="w-[10px] h-[10px] rounded-full border border-black/10 dark:border-white/20"
                  style={{ background: sw }}
                />
                {t.label}
              </button>
            );
          })}
        </div>
      </FieldRow>

      {/* Linked task */}
      {linkedTask && (
        <section className="pt-2 border-t border-line dark:border-dark-line">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-[11px] tracking-[-0.1px] text-fg dark:text-dark-fg font-medium mb-0.5">Verknüpfte Task</div>
              <div className="text-[13px] font-medium text-fg dark:text-dark-fg truncate">{linkedTask.title}</div>
            </div>
            <button
              onClick={() => { onClose(); onOpenTask(linkedTask.id); }}
              className="btn-ghost text-[12px] shrink-0"
            >
              <Icon name="chevronRight" size={12} /> Task öffnen
            </button>
          </div>
        </section>
      )}

      {/* Meeting-Details (Google-Event metadata: URL, Location, Description) */}
      {(block.meetingUrl || block.location || block.description) && (
        <section className="pt-3 border-t border-line dark:border-dark-line space-y-2.5">
          {block.meetingUrl && (
            <a
              href={block.meetingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-accent/40 bg-accent/10 text-accent hover:bg-accent/20 transition text-[12.5px] font-medium max-w-full"
            >
              <Icon name="link" size={12} />
              <span className="truncate">
                {block.meetingUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, '')} öffnen
              </span>
            </a>
          )}
          {block.location && (
            <div className="flex items-start gap-2 text-[12.5px] text-fg dark:text-dark-fg">
              <Icon name="target" size={12} className="mt-0.5 shrink-0 opacity-70" />
              <span className="break-words">{block.location}</span>
            </div>
          )}
          {block.description && (
            <div className="flex items-start gap-2 text-[12px] text-fg dark:text-dark-fg">
              <Icon name="info" size={12} className="mt-0.5 shrink-0 opacity-70" />
              <div className="whitespace-pre-wrap break-words leading-relaxed max-h-[200px] overflow-y-auto opacity-85">
                {block.description}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Inline-Inhalte (Tasks-im-Block) */}
      <BlockContentsSection
        block={block}
        readonly={readonly}
        onPatch={onPatch}
        onOpenTask={(id) => { onClose(); onOpenTask(id); }}
      />

      {/* Google Calendar sync status */}
      {gstatus?.connected && (
        <section className="pt-2 border-t border-line dark:border-dark-line">
          {block.gcalEventId ? (
            <div className="flex items-center gap-2 text-[12px] text-fg dark:text-dark-fg">
              <Icon name="calendar" size={12} className="text-accent" />
              <span>In Google Calendar. Änderungen werden beidseitig synchronisiert.</span>
            </div>
          ) : defaultPushCalId ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => pushToGoogle.mutate(defaultPushCalId)}
                disabled={pushToGoogle.isPending || readonly}
                className="btn-ghost text-[12.5px]"
                title={`Push in Kalender: ${pushableCals[0]?.summary}`}
              >
                <Icon name="calendar" size={12} />
                {pushToGoogle.isPending ? 'Pushe…' : 'Nach Google pushen'}
              </button>
              <span className="text-[11px] text-fg dark:text-dark-fg opacity-70 truncate">
                → {pushableCals[0]?.summary}
              </span>
            </div>
          ) : (
            <div className="text-[11.5px] text-fg dark:text-dark-fg italic">
              Kein Kalender zum Pushen ausgewählt. Öffne Einstellungen → Google Calendar.
            </div>
          )}
        </section>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={onDelete}
          disabled={readonly}
          className={cn('btn-ghost text-bad text-[12.5px]', readonly && 'opacity-50 cursor-not-allowed')}
        >
          <Icon name="trash" size={13} /> Block löschen
        </button>
        <button onClick={onClose} className="btn-primary">Fertig</button>
      </div>
      {readonly && (
        <div className="text-[11.5px] text-fg dark:text-dark-fg italic -mt-2">
          Dieser Termin kommt aus Google Calendar und wird dort verwaltet.
        </div>
      )}
    </div>
  );
}

function BlockContentsSection({
  block,
  readonly,
  onPatch,
  onOpenTask,
}: {
  block: Block;
  readonly: boolean;
  onPatch: (p: Partial<Block>) => void;
  onOpenTask: (taskId: string) => void;
}) {
  const qc = useQueryClient();
  const { data: tasks = [] } = useQuery({ queryKey: ['tasks'], queryFn: api.listTasks });
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const items = Array.isArray(block.contents) ? block.contents : [];
  if (items.length === 0) return null;
  const doneCount = items.filter((c) => c.done).length;

  const toggleTaskDone = useMutation({
    mutationFn: (id: string) => api.toggleTaskDone(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const toggle = (taskId: string) => {
    const cur = items.find((c) => c.taskId === taskId);
    if (!cur) return;
    const next = items.map((c) => (c.taskId === taskId ? { ...c, done: !c.done } : c));
    onPatch({ contents: next } as Partial<Block>);
    // Wenn alle Inline-Items done sind: zugehoerige Tasks ebenfalls als done markieren (best-effort)
    const task = byId.get(taskId);
    if (task && !cur.done && !task.done) {
      toggleTaskDone.mutate(taskId);
    }
  };

  const remove = (taskId: string) => {
    const next = items.filter((c) => c.taskId !== taskId);
    onPatch({ contents: next.length ? next : null } as Partial<Block>);
  };

  return (
    <section className="pt-2 border-t border-line dark:border-dark-line">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] tracking-[-0.1px] text-fg dark:text-dark-fg font-medium">
          Inhalte <span className="font-mono opacity-70">({doneCount}/{items.length})</span>
        </div>
        <div className="text-[10.5px] text-fg dark:text-dark-fg opacity-60">
          Drag Tasks auf den Block um sie hier zu sammeln.
        </div>
      </div>
      <div className="space-y-[3px]">
        {items.map((c) => {
          const t = byId.get(c.taskId);
          const title = t?.title ?? '(gelöschte Aufgabe)';
          return (
            <div
              key={c.taskId}
              className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-line/40 dark:hover:bg-dark-line/40 transition"
            >
              <button
                onClick={() => !readonly && toggle(c.taskId)}
                disabled={readonly}
                className={cn(
                  'shrink-0 w-[14px] h-[14px] rounded-[3px] border flex items-center justify-center',
                  c.done
                    ? 'bg-accent border-accent'
                    : 'border-lineStrong dark:border-dark-lineStrong',
                  readonly && 'opacity-60 cursor-not-allowed',
                )}
                title={c.done ? 'Erledigt' : 'Als erledigt markieren'}
              >
                {c.done && <Icon name="check" size={9} className="text-white" />}
              </button>
              <button
                onClick={() => t && onOpenTask(t.id)}
                disabled={!t}
                className={cn(
                  'flex-1 text-left text-[12.5px] truncate min-w-0 text-fg dark:text-dark-fg',
                  c.done && 'line-through opacity-60',
                  !t && 'italic opacity-60',
                )}
              >
                {title}
              </button>
              {!readonly && (
                <button
                  onClick={() => remove(c.taskId)}
                  className="shrink-0 opacity-40 hover:opacity-100 text-fg dark:text-dark-fg"
                  title="Aus Block entfernen"
                >
                  <Icon name="x" size={11} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ChunksPanel({ task, onPatch }: { task: Task; onPatch: (p: Partial<Task>) => void }) {
  const chunks: Chunk[] = task.chunks ?? [];
  const [newTitle, setNewTitle] = useState('');
  const [newEst, setNewEst] = useState('');
  const totalEstH = chunks.reduce((s, c) => s + parseEst(c.est), 0);

  const setChunks = (next: Chunk[]) => onPatch({ chunks: next });

  const addChunk = () => {
    const t = newTitle.trim();
    if (!t) return;
    const est = newEst.trim() || '30m';
    const id = `ch_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    setChunks([...chunks, { id, title: t, est, done: false }]);
    setNewTitle('');
    setNewEst('');
  };

  const updateChunk = (id: string, patch: Partial<Chunk>) => {
    setChunks(chunks.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };
  const removeChunk = (id: string) => setChunks(chunks.filter((c) => c.id !== id));
  const toggleChunk = (id: string) => {
    const c = chunks.find((x) => x.id === id);
    if (!c) return;
    updateChunk(id, { done: !c.done });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] tracking-[-0.1px] text-fg dark:text-dark-fg font-semibold">
          Chunks {chunks.length > 0 && <span className="text-fg dark:text-dark-fg font-normal">({chunks.filter((c) => c.done).length}/{chunks.length} · {totalEstH.toFixed(1)}h)</span>}
        </div>
      </div>

      {chunks.length === 0 && (
        <div className="text-[12.5px] text-fg dark:text-dark-fg italic pb-2">
          Keine Chunks. Zerleg die Task unten in kleinere Schritte.
        </div>
      )}

      {chunks.length > 0 && (
        <div className="space-y-1 mb-3">
          {chunks.map((c) => (
            <ChunkRow key={c.id} chunk={c} onToggle={() => toggleChunk(c.id)} onRename={(t) => updateChunk(c.id, { title: t })} onEst={(e) => updateChunk(c.id, { est: e })} onRemove={() => removeChunk(c.id)} />
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addChunk(); }}
          placeholder="+ Neuer Chunk"
          className="flex-1 px-3 py-1.5 text-[12.5px] rounded-md border border-dashed border-line dark:border-dark-line bg-transparent input-bare focus:border-accent focus:bg-paper dark:focus:bg-dark-paper"
        />
        <input
          value={newEst}
          onChange={(e) => setNewEst(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addChunk(); }}
          placeholder="30m"
          className="w-[80px] px-2.5 py-1.5 text-[12px] rounded-md border border-dashed border-line dark:border-dark-line bg-transparent input-bare font-mono focus:border-accent focus:bg-paper dark:focus:bg-dark-paper"
        />
        <button onClick={addChunk} disabled={!newTitle.trim()} className="btn-ghost text-[12px] disabled:opacity-40">
          <Icon name="plus" size={12} /> Hinzu
        </button>
      </div>
    </div>
  );
}

function ChunkRow({
  chunk, onToggle, onRename, onEst, onRemove,
}: {
  chunk: Chunk;
  onToggle: () => void;
  onRename: (t: string) => void;
  onEst: (e: string) => void;
  onRemove: () => void;
}) {
  const [t, setT] = useState(chunk.title);
  const [e, setE] = useState(chunk.est);
  useEffect(() => setT(chunk.title), [chunk.title]);
  useEffect(() => setE(chunk.est), [chunk.est]);

  return (
    <div className="flex items-center gap-2 group/chunk px-2 py-1 rounded-md hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition">
      <button
        onClick={onToggle}
        className={cn(
          'shrink-0 w-[14px] h-[14px] rounded-[3px] border grid place-items-center transition',
          chunk.done ? 'bg-accent border-accent' : 'border-lineStrong dark:border-dark-lineStrong hover:border-accent',
        )}
        aria-label={chunk.done ? 'Erledigt rückgängig' : 'Chunk abhaken'}
      >
        {chunk.done && <Icon name="check" size={9} className="text-white" />}
      </button>
      <input
        value={t}
        onChange={(ev) => setT(ev.target.value)}
        onBlur={() => t !== chunk.title && onRename(t)}
        onKeyDown={(ev) => { if (ev.key === 'Enter') (ev.currentTarget as HTMLInputElement).blur(); }}
        className={cn(
          'flex-1 min-w-0 px-1 py-0.5 text-[12.5px] bg-transparent input-bare rounded',
          chunk.done && 'line-through opacity-60',
        )}
      />
      <input
        value={e}
        onChange={(ev) => setE(ev.target.value)}
        onBlur={() => e !== chunk.est && onEst(e)}
        onKeyDown={(ev) => { if (ev.key === 'Enter') (ev.currentTarget as HTMLInputElement).blur(); }}
        className="w-[60px] px-1.5 py-0.5 text-[11px] bg-transparent input-bare text-right font-mono text-fg dark:text-dark-fg"
      />
      <button
        onClick={onRemove}
        className="shrink-0 opacity-0 group-hover/chunk:opacity-100 w-[18px] h-[18px] grid place-items-center rounded hover:bg-bad/20 hover:text-bad transition"
        aria-label="Chunk entfernen"
      >
        <Icon name="x" size={11} />
      </button>
    </div>
  );
}

function fmtHour(h: number) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function fmtRelative(ts: number | null): string {
  if (!ts) return 'nie';
  const dt = Date.now() - ts;
  if (dt < 0) return 'gerade eben';
  const m = Math.round(dt / 60000);
  if (m < 1) return 'gerade eben';
  if (m < 60) return `vor ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `vor ${h} h`;
  const d = Math.round(h / 24);
  return `vor ${d} T`;
}
